import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  useTheme,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Switch,
  Card,
  CardContent,
  Grid,
  Chip,
  CircularProgress,
  Alert,
} from "@mui/material";
import { tokens } from "../../theme";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { base_url } from "../../config/config";

const API_BASE = `${base_url}/v1/simple-app`;

const ACTION_TYPES = [
  { value: "delivery", label: "Lieferung", icon: "car" },
  { value: "incoming", label: "Wareneingang", icon: "arrow-down-circle" },
  { value: "lager", label: "Lager", icon: "cube" },
  { value: "posting", label: "Versand", icon: "send" },
  { value: "contract", label: "Sprachvertrag", icon: "document-text" },
  { value: "custom", label: "Benutzerdefiniert", icon: "ellipsis-horizontal" },
];

const COLOR_OPTIONS = [
  "#4CAF50", // Green
  "#2196F3", // Blue
  "#FF9800", // Orange
  "#9C27B0", // Purple
  "#E91E63", // Pink
  "#00BCD4", // Cyan
  "#795548", // Brown
  "#607D8B", // Blue Grey
  "#F44336", // Red
  "#3F51B5", // Indigo
];

const ButtonConfig = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState("");
  const [buttons, setButtons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingButton, setEditingButton] = useState(null);
  const [formData, setFormData] = useState({
    label: "",
    color: "#4CAF50",
    actionType: "custom",
    requiresPhoto: true,
    requiresDescription: true,
    requiresVersand: false,
    requiresApproval: false,
    order: 0,
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    if (selectedUser) {
      fetchButtons(selectedUser);
    }
  }, [selectedUser]);

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API_BASE}/users`);
      const data = await response.json();
      if (data.success) {
        setUsers(data.data);
        if (data.data.length > 0) {
          setSelectedUser(data.data[0]._id);
        }
      }
    } catch (err) {
      setError("Fehler beim Laden der Benutzer");
    }
  };

  const fetchButtons = async (userId) => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/buttons/user/${userId}`);
      const data = await response.json();
      if (data.success) {
        setButtons(data.data);
      }
    } catch (err) {
      setError("Fehler beim Laden der Buttons");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (button = null) => {
    if (button) {
      setEditingButton(button);
      setFormData({
        label: button.label,
        color: button.color,
        actionType: button.actionType,
        requiresPhoto: button.requiresPhoto,
        requiresDescription: button.requiresDescription,
        requiresVersand: button.requiresVersand || false,
        requiresApproval: button.requiresApproval || false,
        order: button.order,
      });
    } else {
      setEditingButton(null);
      setFormData({
        label: "",
        color: "#4CAF50",
        actionType: "custom",
        requiresPhoto: true,
        requiresDescription: true,
        requiresVersand: false,
        requiresApproval: false,
        order: buttons.length,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingButton(null);
  };

  const handleSave = async () => {
    try {
      const url = editingButton
        ? `${API_BASE}/buttons/${editingButton._id}`
        : `${API_BASE}/buttons`;
      const method = editingButton ? "PUT" : "POST";

      const payload = editingButton
        ? formData
        : { ...formData, userId: selectedUser };

      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (data.success) {
        fetchButtons(selectedUser);
        handleCloseDialog();
      } else {
        setError(data.message || "Fehler beim Speichern");
      }
    } catch (err) {
      setError("Fehler beim Speichern");
    }
  };

  const handleDelete = async (buttonId) => {
    if (!window.confirm("Möchten Sie diesen Button wirklich löschen?")) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/buttons/${buttonId}`, {
        method: "DELETE",
      });
      const data = await response.json();
      if (data.success) {
        fetchButtons(selectedUser);
      }
    } catch (err) {
      setError("Fehler beim Löschen");
    }
  };

  const selectedUserName = users.find((u) => u._id === selectedUser)?.displayName ||
    users.find((u) => u._id === selectedUser)?.name || "";

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="h4" color={colors.grey[100]}>
            Aktions-Buttons
          </Typography>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Benutzer auswählen</InputLabel>
            <Select
              value={selectedUser}
              label="Benutzer auswählen"
              onChange={(e) => setSelectedUser(e.target.value)}
            >
              {users.map((user) => (
                <MenuItem key={user._id} value={user._id}>
                  {user.displayName || user.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Button
          variant="contained"
          color="secondary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          disabled={!selectedUser}
        >
          Neuer Button
        </Button>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={2}>
          {buttons.length === 0 ? (
            <Grid item xs={12}>
              <Typography color={colors.grey[300]} textAlign="center" py={4}>
                Keine Buttons für {selectedUserName}. Erstellen Sie den ersten Button.
              </Typography>
            </Grid>
          ) : (
            buttons.map((button) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={button._id}>
                <Card
                  sx={{
                    backgroundColor: colors.primary[400],
                    borderLeft: `4px solid ${button.color}`,
                  }}
                >
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                      <Box
                        sx={{
                          width: 50,
                          height: 50,
                          borderRadius: 2,
                          backgroundColor: button.color,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#fff",
                          fontWeight: "bold",
                          fontSize: 20,
                        }}
                      >
                        {button.label.charAt(0).toUpperCase()}
                      </Box>
                      <Box>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(button)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(button._id)}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                    <Typography variant="h5" mt={2} fontWeight="bold">
                      {button.label}
                    </Typography>
                    <Chip
                      label={ACTION_TYPES.find((t) => t.value === button.actionType)?.label}
                      size="small"
                      sx={{ mt: 1 }}
                    />
                    <Box mt={2} display="flex" gap={1} flexWrap="wrap">
                      {button.requiresPhoto && (
                        <Chip label="Foto" size="small" variant="outlined" />
                      )}
                      {button.requiresDescription && (
                        <Chip label="Beschreibung" size="small" variant="outlined" />
                      )}
                      {button.requiresVersand && (
                        <Chip label="Versand" size="small" variant="outlined" color="info" />
                      )}
                      {button.requiresApproval && (
                        <Chip label="Freigabe" size="small" variant="outlined" color="warning" />
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))
          )}
        </Grid>
      )}

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingButton ? "Button bearbeiten" : "Neuer Button"}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Button-Text"
              value={formData.label}
              onChange={(e) =>
                setFormData({ ...formData, label: e.target.value })
              }
              fullWidth
              placeholder="z.B. Wareneingang, Lieferung, etc."
            />

            <FormControl fullWidth>
              <InputLabel>Aktionstyp</InputLabel>
              <Select
                value={formData.actionType}
                label="Aktionstyp"
                onChange={(e) =>
                  setFormData({ ...formData, actionType: e.target.value })
                }
              >
                {ACTION_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box>
              <Typography variant="body2" color={colors.grey[300]} mb={1}>
                Button-Farbe
              </Typography>
              <Box display="flex" gap={1} flexWrap="wrap">
                {COLOR_OPTIONS.map((color) => (
                  <Box
                    key={color}
                    onClick={() => setFormData({ ...formData, color })}
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 1,
                      backgroundColor: color,
                      cursor: "pointer",
                      border:
                        formData.color === color
                          ? "3px solid #fff"
                          : "3px solid transparent",
                      boxShadow:
                        formData.color === color
                          ? "0 0 0 2px #000"
                          : "none",
                    }}
                  />
                ))}
              </Box>
            </Box>

            <FormControlLabel
              control={
                <Switch
                  checked={formData.requiresPhoto}
                  onChange={(e) =>
                    setFormData({ ...formData, requiresPhoto: e.target.checked })
                  }
                />
              }
              label="Foto erforderlich"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.requiresDescription}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      requiresDescription: e.target.checked,
                    })
                  }
                />
              }
              label="Beschreibung erforderlich"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.requiresVersand}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      requiresVersand: e.target.checked,
                    })
                  }
                />
              }
              label="Versand-Felder erforderlich"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.requiresApproval}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      requiresApproval: e.target.checked,
                    })
                  }
                />
              }
              label="Freigabe / Unterschrift erforderlich"
            />

            <TextField
              label="Reihenfolge"
              type="number"
              value={formData.order}
              onChange={(e) =>
                setFormData({ ...formData, order: parseInt(e.target.value) || 0 })
              }
              fullWidth
              helperText="Niedrigere Zahlen werden zuerst angezeigt"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Abbrechen</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            color="secondary"
            disabled={!formData.label.trim()}
          >
            Speichern
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ButtonConfig;
